import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, addDays } from "date-fns";
import { employeeApi, shiftApi, absenceApi, businessHoursApi, breakTypeApi, eventsApi } from "../lib/api";
import { getColorById, getCurrencySymbol, calculateShiftCost, calculateTotalHours, calculateBillableHours, getSalaryDailyCost, calculatePaidAbsenceDailyCost } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { BarChart3, CalendarIcon, Download, FileText, Users, Clock, DollarSign, CalendarDays, ChevronDown, Lock, Check, ArrowRight, Sparkles, PartyPopper, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { useAuth } from "../lib/AuthContext";
import { FeatureGate, FeatureButton } from "../components/FeatureGate";

// Feature metadata for UI display
const FEATURE_METADATA = {
  advancedReports: { name: 'Advanced Reports', minTier: 'standard' },
  csvExport: { name: 'CSV Export', minTier: 'standard' },
  pdfExport: { name: 'PDF Export', minTier: 'standard' },
  wageCostCalculations: { name: 'Wage Cost Calculations', minTier: 'standard' },
  absenceTracking: { name: 'Absence & Leave Tracking', minTier: 'standard' },
  eventManagement: { name: 'Event Management', minTier: 'standard' }
};

// Date range presets
const DATE_PRESETS = [
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "custom", label: "Custom Range" },
];

const Reports = () => {
  const navigate = useNavigate();
  const { subscriptionTier, hasFeatureAccess } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [events, setEvents] = useState([]);
  const [breakTypes, setBreakTypes] = useState([]);
  const [currency, setCurrency] = useState("GBP");
  const [loading, setLoading] = useState(true);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, feature: null });
  
  // Get current tier for feature checking using dynamic flags
  const currentTier = subscriptionTier || 'light';
  const hasAdvancedReports = hasFeatureAccess('advancedReports');
  const hasCsvExport = hasFeatureAccess('csvExport');
  const hasPdfExport = hasFeatureAccess('pdfExport');
  const hasWageCosts = hasFeatureAccess('wageCostCalculations');
  const hasAbsenceAccess = hasFeatureAccess('absenceTracking');
  const hasEventAccess = hasFeatureAccess('eventManagement');
  
  // Filters
  const [datePreset, setDatePreset] = useState("this_week");
  const [customStartDate, setCustomStartDate] = useState(new Date());
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [groupBy, setGroupBy] = useState("employee"); // "employee" or "date"
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  
  // Active report tab
  const [activeReport, setActiveReport] = useState("hours");

  // Calculate date range based on preset
  const dateRange = useMemo(() => {
    const today = new Date();
    
    switch (datePreset) {
      case "this_week":
        return {
          start: startOfWeek(today, { weekStartsOn: 1 }),
          end: endOfWeek(today, { weekStartsOn: 1 }),
        };
      case "last_week":
        const lastWeek = subWeeks(today, 1);
        return {
          start: startOfWeek(lastWeek, { weekStartsOn: 1 }),
          end: endOfWeek(lastWeek, { weekStartsOn: 1 }),
        };
      case "this_month":
        return {
          start: startOfMonth(today),
          end: endOfMonth(today),
        };
      case "last_month":
        const lastMonth = subMonths(today, 1);
        return {
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth),
        };
      case "custom":
        return {
          start: customStartDate,
          end: customEndDate,
        };
      default:
        return {
          start: startOfWeek(today, { weekStartsOn: 1 }),
          end: endOfWeek(today, { weekStartsOn: 1 }),
        };
    }
  }, [datePreset, customStartDate, customEndDate]);

  const startStr = format(dateRange.start, "yyyy-MM-dd");
  const endStr = format(dateRange.end, "yyyy-MM-dd");

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [empRes, shiftRes, absenceRes, breakRes, hoursRes, eventsRes] = await Promise.all([
          employeeApi.getAll(),
          shiftApi.getAll(startStr, endStr),
          absenceApi.getAll(null, startStr, endStr),
          breakTypeApi.getAll(),
          businessHoursApi.get(),
          eventsApi.getAll(startStr, endStr),
        ]);
        
        setEmployees(empRes.data);
        setShifts(shiftRes.data);
        setAbsences(absenceRes.data);
        setBreakTypes(breakRes.data);
        setCurrency(hoursRes.data?.currency || "GBP");
        setEvents(eventsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load report data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [startStr, endStr]);

  // Filter data by selected employee
  const filteredShifts = useMemo(() => {
    if (selectedEmployee === "all") return shifts;
    return shifts.filter(s => s.employee_id === selectedEmployee);
  }, [shifts, selectedEmployee]);

  const filteredAbsences = useMemo(() => {
    if (selectedEmployee === "all") return absences;
    return absences.filter(a => a.employee_id === selectedEmployee);
  }, [absences, selectedEmployee]);

  // Get number of days in the range
  const daysInRange = useMemo(() => {
    const days = [];
    let current = new Date(dateRange.start);
    while (current <= dateRange.end) {
      days.push(format(current, "yyyy-MM-dd"));
      current = addDays(current, 1);
    }
    return days;
  }, [dateRange]);

  // Calculate Hours Summary data
  const hoursSummary = useMemo(() => {
    const employeeList = selectedEmployee === "all" 
      ? employees 
      : employees.filter(e => e.id === selectedEmployee);
    
    if (groupBy === "employee") {
      return employeeList.map(emp => {
        const empShifts = filteredShifts.filter(s => s.employee_id === emp.id);
        const totalHours = calculateTotalHours(empShifts);
        const billableHours = empShifts.reduce((acc, shift) => {
          return acc + calculateBillableHours(shift, breakTypes);
        }, 0);
        const shiftCount = empShifts.length;
        
        return {
          id: emp.id,
          name: emp.name,
          role: emp.role,
          color: emp.color,
          totalHours,
          billableHours,
          shiftCount,
        };
      }).filter(emp => emp.shiftCount > 0 || selectedEmployee !== "all");
    } else {
      // Group by date
      return daysInRange.map(date => {
        const dayShifts = filteredShifts.filter(s => s.date === date);
        const totalHours = calculateTotalHours(dayShifts);
        const billableHours = dayShifts.reduce((acc, shift) => {
          return acc + calculateBillableHours(shift, breakTypes);
        }, 0);
        const employeesWorking = new Set(dayShifts.map(s => s.employee_id)).size;
        
        return {
          date,
          displayDate: format(new Date(date), "EEE, MMM d"),
          totalHours,
          billableHours,
          shiftCount: dayShifts.length,
          employeesWorking,
        };
      });
    }
  }, [employees, filteredShifts, breakTypes, groupBy, selectedEmployee, daysInRange]);

  // Calculate Cost Summary data
  const costSummary = useMemo(() => {
    const employeeList = selectedEmployee === "all" 
      ? employees 
      : employees.filter(e => e.id === selectedEmployee);
    
    if (groupBy === "employee") {
      return employeeList.map(emp => {
        const empShifts = filteredShifts.filter(s => s.employee_id === emp.id);
        const isSalaryEmployee = emp.pay_type === "salary";
        
        // Calculate shift costs
        const shiftCost = isSalaryEmployee ? 0 : empShifts.reduce((acc, shift) => {
          return acc + calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, emp);
        }, 0);
        
        // Calculate salary cost (for salary employees)
        let salaryCost = 0;
        if (isSalaryEmployee) {
          daysInRange.forEach(date => {
            const isOnUnpaidLeave = absences.some(a => 
              a.employee_id === emp.id && 
              !a.is_paid && 
              a.start_date <= date && 
              a.end_date >= date
            );
            if (!isOnUnpaidLeave) {
              salaryCost += getSalaryDailyCost(emp);
            }
          });
        }
        
        // Calculate paid absence cost
        let absenceCost = 0;
        absences.filter(a => a.employee_id === emp.id && a.is_paid).forEach(absence => {
          // Count days in this absence that fall within the date range
          daysInRange.forEach(date => {
            if (date >= absence.start_date && date <= absence.end_date) {
              absenceCost += calculatePaidAbsenceDailyCost(absence, emp);
            }
          });
        });
        
        const totalCost = shiftCost + salaryCost + absenceCost;
        const totalHours = calculateTotalHours(empShifts);
        
        return {
          id: emp.id,
          name: emp.name,
          role: emp.role,
          color: emp.color,
          payType: emp.pay_type || "hourly",
          shiftCost,
          salaryCost,
          absenceCost,
          totalCost,
          totalHours,
        };
      }).filter(emp => emp.totalCost > 0 || selectedEmployee !== "all");
    } else {
      // Group by date
      return daysInRange.map(date => {
        let dayCost = 0;
        let shiftCost = 0;
        let salaryCost = 0;
        let absenceCost = 0;
        
        const dayShifts = filteredShifts.filter(s => s.date === date);
        
        employeeList.forEach(emp => {
          const empDayShifts = dayShifts.filter(s => s.employee_id === emp.id);
          const isSalaryEmployee = emp.pay_type === "salary";
          
          // Shift costs for hourly employees
          if (!isSalaryEmployee) {
            empDayShifts.forEach(shift => {
              shiftCost += calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, emp);
            });
          }
          
          // Salary cost
          if (isSalaryEmployee) {
            const isOnUnpaidLeave = absences.some(a => 
              a.employee_id === emp.id && 
              !a.is_paid && 
              a.start_date <= date && 
              a.end_date >= date
            );
            if (!isOnUnpaidLeave) {
              salaryCost += getSalaryDailyCost(emp);
            }
          }
          
          // Paid absence cost
          absences.filter(a => a.employee_id === emp.id && a.is_paid).forEach(absence => {
            if (date >= absence.start_date && date <= absence.end_date) {
              absenceCost += calculatePaidAbsenceDailyCost(absence, emp);
            }
          });
        });
        
        dayCost = shiftCost + salaryCost + absenceCost;
        const totalHours = calculateTotalHours(dayShifts);
        
        return {
          date,
          displayDate: format(new Date(date), "EEE, MMM d"),
          shiftCost,
          salaryCost,
          absenceCost,
          totalCost: dayCost,
          totalHours,
          shiftCount: dayShifts.length,
        };
      });
    }
  }, [employees, filteredShifts, absences, breakTypes, groupBy, selectedEmployee, daysInRange]);

  // Shift Details data
  const shiftDetails = useMemo(() => {
    return filteredShifts.map(shift => {
      const emp = employees.find(e => e.id === shift.employee_id);
      if (!emp) return null;
      
      const totalHours = calculateTotalHours([shift]);
      const billableHours = calculateBillableHours(shift, breakTypes);
      const cost = calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, emp);
      
      return {
        id: shift.id,
        date: shift.date,
        displayDate: format(new Date(shift.date), "EEE, MMM d"),
        employeeName: emp.name,
        employeeRole: emp.role,
        employeeColor: emp.color,
        startTime: shift.start_time,
        endTime: shift.end_time,
        totalHours,
        billableHours,
        cost,
        rateName: shift.rate_name || "Standard",
        breakCount: shift.breaks?.length || 0,
      };
    }).filter(Boolean).sort((a, b) => {
      if (groupBy === "date") {
        return a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime);
      }
      return a.employeeName.localeCompare(b.employeeName) || a.date.localeCompare(b.date);
    });
  }, [filteredShifts, employees, breakTypes, groupBy]);

  // Absence Summary data
  const absenceSummary = useMemo(() => {
    const employeeList = selectedEmployee === "all" 
      ? employees 
      : employees.filter(e => e.id === selectedEmployee);
    
    if (groupBy === "employee") {
      return employeeList.map(emp => {
        const empAbsences = filteredAbsences.filter(a => a.employee_id === emp.id);
        
        let totalDays = 0;
        let paidDays = 0;
        let unpaidDays = 0;
        let totalCost = 0;
        
        empAbsences.forEach(absence => {
          // Count days that fall within our date range
          daysInRange.forEach(date => {
            if (date >= absence.start_date && date <= absence.end_date) {
              totalDays++;
              if (absence.is_paid) {
                paidDays++;
                totalCost += calculatePaidAbsenceDailyCost(absence, emp);
              } else {
                unpaidDays++;
              }
            }
          });
        });
        
        return {
          id: emp.id,
          name: emp.name,
          role: emp.role,
          color: emp.color,
          absenceCount: empAbsences.length,
          totalDays,
          paidDays,
          unpaidDays,
          totalCost,
        };
      }).filter(emp => emp.totalDays > 0 || selectedEmployee !== "all");
    } else {
      // Group by date
      return daysInRange.map(date => {
        const dayAbsences = filteredAbsences.filter(a => 
          a.start_date <= date && a.end_date >= date
        );
        
        let totalCost = 0;
        let paidCount = 0;
        let unpaidCount = 0;
        
        dayAbsences.forEach(absence => {
          const emp = employees.find(e => e.id === absence.employee_id);
          if (absence.is_paid && emp) {
            paidCount++;
            totalCost += calculatePaidAbsenceDailyCost(absence, emp);
          } else {
            unpaidCount++;
          }
        });
        
        return {
          date,
          displayDate: format(new Date(date), "EEE, MMM d"),
          employeesAbsent: dayAbsences.length,
          paidCount,
          unpaidCount,
          totalCost,
        };
      }).filter(day => day.employeesAbsent > 0);
    }
  }, [employees, filteredAbsences, groupBy, selectedEmployee, daysInRange]);

  // Calculate Events Summary
  const eventsSummary = useMemo(() => {
    // Helper to calculate event duration in hours
    const calculateEventHours = (event) => {
      const [startH, startM] = event.start_time.split(':').map(Number);
      const [endH, endM] = event.end_time.split(':').map(Number);
      const startMins = startH * 60 + startM;
      const endMins = endH * 60 + endM;
      return (endMins - startMins) / 60;
    };

    if (groupBy === "employee") {
      // Group by event name (aggregate same-named events)
      const eventMap = new Map();
      
      events.forEach(event => {
        const hours = calculateEventHours(event);
        const existing = eventMap.get(event.name);
        
        if (existing) {
          existing.totalHours += hours;
          existing.count += 1;
        } else {
          eventMap.set(event.name, {
            name: event.name,
            color: event.color,
            totalHours: hours,
            count: 1,
          });
        }
      });
      
      return Array.from(eventMap.values()).sort((a, b) => b.totalHours - a.totalHours);
    } else {
      // Group by date
      return daysInRange.map(date => {
        const dayEvents = events.filter(e => e.date === date);
        const totalHours = dayEvents.reduce((acc, event) => acc + calculateEventHours(event), 0);
        
        return {
          date,
          displayDate: format(new Date(date), "EEE, MMM d"),
          eventCount: dayEvents.length,
          totalHours,
        };
      }).filter(day => day.eventCount > 0);
    }
  }, [events, groupBy, daysInRange]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalHours = hoursSummary.reduce((acc, item) => acc + (item.totalHours || 0), 0);
    const totalBillableHours = hoursSummary.reduce((acc, item) => acc + (item.billableHours || 0), 0);
    const totalCost = costSummary.reduce((acc, item) => acc + (item.totalCost || 0), 0);
    const totalShiftCost = costSummary.reduce((acc, item) => acc + (item.shiftCost || 0), 0);
    const totalSalaryCost = costSummary.reduce((acc, item) => acc + (item.salaryCost || 0), 0);
    const totalAbsenceCost = costSummary.reduce((acc, item) => acc + (item.absenceCost || 0), 0);
    const totalAbsenceDays = absenceSummary.reduce((acc, item) => acc + (item.totalDays || item.employeesAbsent || 0), 0);
    const totalEvents = events.length;
    const totalEventHours = eventsSummary.reduce((acc, item) => acc + (item.totalHours || 0), 0);
    
    return {
      totalHours,
      totalBillableHours,
      totalCost,
      totalShiftCost,
      totalSalaryCost,
      totalAbsenceCost,
      totalAbsenceDays,
      totalEvents,
      totalEventHours,
      shiftCount: filteredShifts.length,
    };
  }, [hoursSummary, costSummary, absenceSummary, filteredShifts, events, eventsSummary]);

  const currencySymbol = getCurrencySymbol(currency);

  // Export to CSV
  const exportToCSV = (reportType) => {
    let csvContent = "";
    let filename = "";
    
    switch (reportType) {
      case "hours":
        filename = `hours-report-${startStr}-to-${endStr}.csv`;
        if (groupBy === "employee") {
          csvContent = "Employee,Role,Total Hours,Billable Hours,Shift Count\n";
          hoursSummary.forEach(row => {
            csvContent += `"${row.name}","${row.role}",${row.totalHours.toFixed(2)},${row.billableHours.toFixed(2)},${row.shiftCount}\n`;
          });
        } else {
          csvContent = "Date,Total Hours,Billable Hours,Shift Count,Employees Working\n";
          hoursSummary.forEach(row => {
            csvContent += `"${row.displayDate}",${row.totalHours.toFixed(2)},${row.billableHours.toFixed(2)},${row.shiftCount},${row.employeesWorking}\n`;
          });
        }
        break;
        
      case "costs":
        filename = `cost-report-${startStr}-to-${endStr}.csv`;
        if (groupBy === "employee") {
          csvContent = "Employee,Role,Pay Type,Shift Cost,Salary Cost,Absence Cost,Total Cost,Total Hours\n";
          costSummary.forEach(row => {
            csvContent += `"${row.name}","${row.role}","${row.payType}",${row.shiftCost.toFixed(2)},${row.salaryCost.toFixed(2)},${row.absenceCost.toFixed(2)},${row.totalCost.toFixed(2)},${row.totalHours.toFixed(2)}\n`;
          });
        } else {
          csvContent = "Date,Shift Cost,Salary Cost,Absence Cost,Total Cost,Total Hours,Shift Count\n";
          costSummary.forEach(row => {
            csvContent += `"${row.displayDate}",${row.shiftCost.toFixed(2)},${row.salaryCost.toFixed(2)},${row.absenceCost.toFixed(2)},${row.totalCost.toFixed(2)},${row.totalHours.toFixed(2)},${row.shiftCount}\n`;
          });
        }
        break;
        
      case "shifts":
        filename = `shift-details-${startStr}-to-${endStr}.csv`;
        csvContent = "Date,Employee,Role,Start Time,End Time,Total Hours,Billable Hours,Rate,Breaks,Cost\n";
        shiftDetails.forEach(row => {
          csvContent += `"${row.displayDate}","${row.employeeName}","${row.employeeRole}","${row.startTime}","${row.endTime}",${row.totalHours.toFixed(2)},${row.billableHours.toFixed(2)},"${row.rateName}",${row.breakCount},${row.cost.toFixed(2)}\n`;
        });
        break;
        
      case "absences":
        filename = `absence-report-${startStr}-to-${endStr}.csv`;
        if (groupBy === "employee") {
          csvContent = "Employee,Role,Total Days,Paid Days,Unpaid Days,Total Cost\n";
          absenceSummary.forEach(row => {
            csvContent += `"${row.name}","${row.role}",${row.totalDays},${row.paidDays},${row.unpaidDays},${row.totalCost.toFixed(2)}\n`;
          });
        } else {
          csvContent = "Date,Employees Absent,Paid,Unpaid,Total Cost\n";
          absenceSummary.forEach(row => {
            csvContent += `"${row.displayDate}",${row.employeesAbsent},${row.paidCount},${row.unpaidCount},${row.totalCost.toFixed(2)}\n`;
          });
        }
        break;
    }
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    toast.success(`Exported ${filename}`);
  };

  // Export to PDF (simple print-friendly version)
  const exportToPDF = (reportType) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow popups to export PDF");
      return;
    }
    
    let title = "";
    let tableHTML = "";
    
    switch (reportType) {
      case "hours":
        title = "Hours Report";
        if (groupBy === "employee") {
          tableHTML = `
            <table>
              <thead>
                <tr><th>Employee</th><th>Role</th><th>Total Hours</th><th>Billable Hours</th><th>Shifts</th></tr>
              </thead>
              <tbody>
                ${hoursSummary.map(row => `
                  <tr>
                    <td>${row.name}</td>
                    <td>${row.role}</td>
                    <td>${row.totalHours.toFixed(2)}h</td>
                    <td>${row.billableHours.toFixed(2)}h</td>
                    <td>${row.shiftCount}</td>
                  </tr>
                `).join("")}
                <tr class="total">
                  <td colspan="2"><strong>Total</strong></td>
                  <td><strong>${totals.totalHours.toFixed(2)}h</strong></td>
                  <td><strong>${totals.totalBillableHours.toFixed(2)}h</strong></td>
                  <td><strong>${totals.shiftCount}</strong></td>
                </tr>
              </tbody>
            </table>
          `;
        } else {
          tableHTML = `
            <table>
              <thead>
                <tr><th>Date</th><th>Total Hours</th><th>Billable Hours</th><th>Shifts</th><th>Employees</th></tr>
              </thead>
              <tbody>
                ${hoursSummary.map(row => `
                  <tr>
                    <td>${row.displayDate}</td>
                    <td>${row.totalHours.toFixed(2)}h</td>
                    <td>${row.billableHours.toFixed(2)}h</td>
                    <td>${row.shiftCount}</td>
                    <td>${row.employeesWorking}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          `;
        }
        break;
        
      case "costs":
        title = "Cost Report";
        if (groupBy === "employee") {
          tableHTML = `
            <table>
              <thead>
                <tr><th>Employee</th><th>Pay Type</th><th>Shift Cost</th><th>Salary</th><th>Absences</th><th>Total</th></tr>
              </thead>
              <tbody>
                ${costSummary.map(row => `
                  <tr>
                    <td>${row.name}</td>
                    <td>${row.payType}</td>
                    <td>${currencySymbol}${row.shiftCost.toFixed(2)}</td>
                    <td>${currencySymbol}${row.salaryCost.toFixed(2)}</td>
                    <td>${currencySymbol}${row.absenceCost.toFixed(2)}</td>
                    <td><strong>${currencySymbol}${row.totalCost.toFixed(2)}</strong></td>
                  </tr>
                `).join("")}
                <tr class="total">
                  <td colspan="2"><strong>Total</strong></td>
                  <td><strong>${currencySymbol}${totals.totalShiftCost.toFixed(2)}</strong></td>
                  <td><strong>${currencySymbol}${totals.totalSalaryCost.toFixed(2)}</strong></td>
                  <td><strong>${currencySymbol}${totals.totalAbsenceCost.toFixed(2)}</strong></td>
                  <td><strong>${currencySymbol}${totals.totalCost.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          `;
        } else {
          tableHTML = `
            <table>
              <thead>
                <tr><th>Date</th><th>Shift Cost</th><th>Salary</th><th>Absences</th><th>Total</th></tr>
              </thead>
              <tbody>
                ${costSummary.map(row => `
                  <tr>
                    <td>${row.displayDate}</td>
                    <td>${currencySymbol}${row.shiftCost.toFixed(2)}</td>
                    <td>${currencySymbol}${row.salaryCost.toFixed(2)}</td>
                    <td>${currencySymbol}${row.absenceCost.toFixed(2)}</td>
                    <td><strong>${currencySymbol}${row.totalCost.toFixed(2)}</strong></td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          `;
        }
        break;
        
      case "shifts":
        title = "Shift Details";
        tableHTML = `
          <table>
            <thead>
              <tr><th>Date</th><th>Employee</th><th>Time</th><th>Hours</th><th>Rate</th><th>Cost</th></tr>
            </thead>
            <tbody>
              ${shiftDetails.map(row => `
                <tr>
                  <td>${row.displayDate}</td>
                  <td>${row.employeeName}</td>
                  <td>${row.startTime} - ${row.endTime}</td>
                  <td>${row.billableHours.toFixed(2)}h</td>
                  <td>${row.rateName}</td>
                  <td>${currencySymbol}${row.cost.toFixed(2)}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        `;
        break;
        
      case "absences":
        title = "Absence Report";
        if (groupBy === "employee") {
          tableHTML = `
            <table>
              <thead>
                <tr><th>Employee</th><th>Role</th><th>Total Days</th><th>Paid</th><th>Unpaid</th><th>Cost</th></tr>
              </thead>
              <tbody>
                ${absenceSummary.map(row => `
                  <tr>
                    <td>${row.name}</td>
                    <td>${row.role}</td>
                    <td>${row.totalDays}</td>
                    <td>${row.paidDays}</td>
                    <td>${row.unpaidDays}</td>
                    <td>${currencySymbol}${row.totalCost.toFixed(2)}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          `;
        } else {
          tableHTML = `
            <table>
              <thead>
                <tr><th>Date</th><th>Employees Absent</th><th>Paid</th><th>Unpaid</th><th>Cost</th></tr>
              </thead>
              <tbody>
                ${absenceSummary.map(row => `
                  <tr>
                    <td>${row.displayDate}</td>
                    <td>${row.employeesAbsent}</td>
                    <td>${row.paidCount}</td>
                    <td>${row.unpaidCount}</td>
                    <td>${currencySymbol}${row.totalCost.toFixed(2)}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          `;
        }
        break;
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title} - Rotalie</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; margin-bottom: 5px; }
            .subtitle { color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background: #f5f5f5; font-weight: 600; }
            tr:nth-child(even) { background: #fafafa; }
            .total { background: #e8e8e8 !important; font-weight: 600; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <p class="subtitle">${format(dateRange.start, "MMM d, yyyy")} - ${format(dateRange.end, "MMM d, yyyy")} | ${groupBy === "employee" ? "Grouped by Employee" : "Grouped by Date"}</p>
          ${tableHTML}
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Reports</h2>
          <p className="text-muted-foreground">
            View and export detailed reports on hours, costs, and absences
          </p>
        </div>
        <Button onClick={() => navigate('/dashboard/reports/charts')} data-testid="charts-btn">
          <TrendingUp className="w-4 h-4 mr-2" />
          Charts
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            {/* Date Range Preset */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Date Range</label>
              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger className="w-40" data-testid="date-preset-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESETS.map(preset => (
                    <SelectItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Custom Date Pickers */}
            {datePreset === "custom" && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">From</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-36 justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(customStartDate, "MMM d, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={customStartDate}
                        onSelect={(date) => date && setCustomStartDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">To</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-36 justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(customEndDate, "MMM d, yyyy")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={customEndDate}
                        onSelect={(date) => date && setCustomEndDate(date)}
                        disabled={(date) => date < customStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </>
            )}

            {/* Employee Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Employee</label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="w-44" data-testid="employee-filter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Group By */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Group By</label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-36" data-testid="group-by-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Date Range Display */}
            <div className="ml-auto text-sm text-muted-foreground">
              {format(dateRange.start, "MMM d")} - {format(dateRange.end, "MMM d, yyyy")}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">Total Hours</span>
            </div>
            <p className="text-2xl font-bold tabular-nums">{totals.totalHours.toFixed(2)}h</p>
          </CardContent>
        </Card>
        {hasWageCosts ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm font-medium">Total Cost</span>
              </div>
              <p className="text-2xl font-bold tabular-nums">{currencySymbol}{totals.totalCost.toFixed(2)}</p>
            </CardContent>
          </Card>
        ) : (
          <Card 
            className="relative overflow-hidden cursor-pointer hover:border-blue-500/50 transition-colors"
            onClick={() => setUpgradeModal({ open: true, feature: 'wageCostCalculations' })}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm font-medium">Total Cost</span>
              </div>
              <p className="text-2xl font-bold tabular-nums text-muted-foreground/30">£---.--</p>
            </CardContent>
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[1px] pointer-events-none">
              <div className="flex flex-col items-center gap-1 text-center px-2">
                <Lock className="w-5 h-5 text-muted-foreground" />
                <p className="text-xs text-muted-foreground font-medium">Upgrade to Standard</p>
              </div>
            </div>
          </Card>
        )}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm font-medium">Total Shifts</span>
            </div>
            <p className="text-2xl font-bold tabular-nums">{totals.shiftCount}</p>
          </CardContent>
        </Card>
        {hasEventAccess ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <PartyPopper className="w-4 h-4" />
                <span className="text-sm font-medium">Events</span>
              </div>
              <p className="text-2xl font-bold tabular-nums">{totals.totalEvents}</p>
            </CardContent>
          </Card>
        ) : (
          <Card 
            className="relative overflow-hidden cursor-pointer hover:border-blue-500/50 transition-colors"
            onClick={() => setUpgradeModal({ open: true, feature: 'eventManagement' })}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <PartyPopper className="w-4 h-4" />
                <span className="text-sm font-medium">Events</span>
              </div>
              <p className="text-2xl font-bold tabular-nums text-muted-foreground/30">--</p>
            </CardContent>
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[1px] pointer-events-none">
              <div className="flex flex-col items-center gap-1 text-center px-2">
                <Lock className="w-5 h-5 text-muted-foreground" />
                <p className="text-xs text-muted-foreground font-medium">Upgrade to Standard</p>
              </div>
            </div>
          </Card>
        )}
        {hasAbsenceAccess ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <CalendarDays className="w-4 h-4" />
                <span className="text-sm font-medium">Absence Days</span>
              </div>
              <p className="text-2xl font-bold tabular-nums">{totals.totalAbsenceDays}</p>
            </CardContent>
          </Card>
        ) : (
          <Card 
            className="relative overflow-hidden cursor-pointer hover:border-blue-500/50 transition-colors"
            onClick={() => setUpgradeModal({ open: true, feature: 'absenceTracking' })}
          >
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <CalendarDays className="w-4 h-4" />
                <span className="text-sm font-medium">Absence Days</span>
              </div>
              <p className="text-2xl font-bold tabular-nums text-muted-foreground/30">--</p>
            </CardContent>
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-[1px] pointer-events-none">
              <div className="flex flex-col items-center gap-1 text-center px-2">
                <Lock className="w-5 h-5 text-muted-foreground" />
                <p className="text-xs text-muted-foreground font-medium">Upgrade to Standard</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Report Tabs */}
      <Card>
        <CardContent className="p-0">
          <Tabs value={activeReport} onValueChange={setActiveReport}>
            <div className="flex items-center justify-between border-b px-4">
              <TabsList className="h-14 bg-transparent">
                <TabsTrigger value="hours" className="data-[state=active]:bg-muted" data-testid="tab-hours">
                  <Clock className="w-4 h-4 mr-2" />
                  Hours Summary
                </TabsTrigger>
                {hasWageCosts ? (
                  <TabsTrigger value="costs" className="data-[state=active]:bg-muted" data-testid="tab-costs">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Cost Summary
                  </TabsTrigger>
                ) : (
                  <FeatureButton feature="wageCostCalculations" variant="ghost" size="sm" className="h-9 px-3">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Cost Summary
                  </FeatureButton>
                )}
                {hasAdvancedReports ? (
                  <TabsTrigger value="shifts" className="data-[state=active]:bg-muted" data-testid="tab-shifts">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Shift Details
                  </TabsTrigger>
                ) : (
                  <FeatureButton feature="advancedReports" variant="ghost" size="sm" className="h-9 px-3">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Shift Details
                  </FeatureButton>
                )}
                {hasAdvancedReports ? (
                  <TabsTrigger value="absences" className="data-[state=active]:bg-muted" data-testid="tab-absences">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Absences
                  </TabsTrigger>
                ) : (
                  <FeatureButton feature="advancedReports" variant="ghost" size="sm" className="h-9 px-3">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Absences
                  </FeatureButton>
                )}
                {hasEventAccess ? (
                  <TabsTrigger value="events" className="data-[state=active]:bg-muted" data-testid="tab-events">
                    <PartyPopper className="w-4 h-4 mr-2" />
                    Events
                  </TabsTrigger>
                ) : (
                  <FeatureButton feature="eventManagement" variant="ghost" size="sm" className="h-9 px-3">
                    <PartyPopper className="w-4 h-4 mr-2" />
                    Events
                  </FeatureButton>
                )}
              </TabsList>
              
              {/* Export Buttons */}
              <div className="flex items-center gap-2">
                {hasCsvExport ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => exportToCSV(activeReport)}
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                ) : (
                  <FeatureButton feature="csvExport" variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </FeatureButton>
                )}
                {hasPdfExport ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => exportToPDF(activeReport)}
                    data-testid="export-pdf-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                ) : (
                  <FeatureButton feature="pdfExport" variant="outline" size="sm">
                    <FileText className="w-4 h-4 mr-2" />
                    PDF
                  </FeatureButton>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                {/* Hours Summary Tab */}
                <TabsContent value="hours" className="m-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {groupBy === "employee" ? (
                          <>
                            <TableHead>Employee</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead className="text-right">Total Hours</TableHead>
                            <TableHead className="text-right">Billable Hours</TableHead>
                            <TableHead className="text-right">Shifts</TableHead>
                          </>
                        ) : (
                          <>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Total Hours</TableHead>
                            <TableHead className="text-right">Billable Hours</TableHead>
                            <TableHead className="text-right">Shifts</TableHead>
                            <TableHead className="text-right">Employees</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hoursSummary.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            No hours data for this period
                          </TableCell>
                        </TableRow>
                      ) : groupBy === "employee" ? (
                        hoursSummary.map(row => {
                          const color = getColorById(row.color);
                          return (
                            <TableRow key={row.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {row.avatar_url ? (
                                    <img 
                                      src={row.avatar_url} 
                                      alt={row.name}
                                      className="w-8 h-8 rounded-full object-cover border border-border"
                                    />
                                  ) : (
                                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium", color?.bg, color?.text)}>
                                      {row.name.charAt(0)}
                                    </div>
                                  )}
                                  <span className="font-medium">{row.name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground">{row.role}</TableCell>
                              <TableCell className="text-right tabular-nums">{row.totalHours.toFixed(2)}h</TableCell>
                              <TableCell className="text-right tabular-nums">{row.billableHours.toFixed(2)}h</TableCell>
                              <TableCell className="text-right">{row.shiftCount}</TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        hoursSummary.map(row => (
                          <TableRow key={row.date}>
                            <TableCell className="font-medium">{row.displayDate}</TableCell>
                            <TableCell className="text-right tabular-nums">{row.totalHours.toFixed(2)}h</TableCell>
                            <TableCell className="text-right tabular-nums">{row.billableHours.toFixed(2)}h</TableCell>
                            <TableCell className="text-right">{row.shiftCount}</TableCell>
                            <TableCell className="text-right">{row.employeesWorking}</TableCell>
                          </TableRow>
                        ))
                      )}
                      {hoursSummary.length > 0 && (
                        <TableRow className="bg-muted/50 font-medium">
                          <TableCell colSpan={groupBy === "employee" ? 2 : 1}>Total</TableCell>
                          <TableCell className="text-right tabular-nums">{totals.totalHours.toFixed(2)}h</TableCell>
                          <TableCell className="text-right tabular-nums">{totals.totalBillableHours.toFixed(2)}h</TableCell>
                          <TableCell className="text-right">{totals.shiftCount}</TableCell>
                          {groupBy === "date" && <TableCell></TableCell>}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TabsContent>

                {/* Cost Summary Tab */}
                <TabsContent value="costs" className="m-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {groupBy === "employee" ? (
                          <>
                            <TableHead>Employee</TableHead>
                            <TableHead>Pay Type</TableHead>
                            <TableHead className="text-right">Shift Cost</TableHead>
                            <TableHead className="text-right">Salary</TableHead>
                            <TableHead className="text-right">Absences</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </>
                        ) : (
                          <>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Shift Cost</TableHead>
                            <TableHead className="text-right">Salary</TableHead>
                            <TableHead className="text-right">Absences</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costSummary.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                            No cost data for this period
                          </TableCell>
                        </TableRow>
                      ) : groupBy === "employee" ? (
                        costSummary.map(row => {
                          const color = getColorById(row.color);
                          return (
                            <TableRow key={row.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium", color?.bg, color?.text)}>
                                    {row.name.charAt(0)}
                                  </div>
                                  <span className="font-medium">{row.name}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant={row.payType === "salary" ? "secondary" : "outline"}>
                                  {row.payType}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{currencySymbol}{row.shiftCost.toFixed(2)}</TableCell>
                              <TableCell className="text-right tabular-nums">{currencySymbol}{row.salaryCost.toFixed(2)}</TableCell>
                              <TableCell className="text-right tabular-nums">{currencySymbol}{row.absenceCost.toFixed(2)}</TableCell>
                              <TableCell className="text-right tabular-nums font-medium">{currencySymbol}{row.totalCost.toFixed(2)}</TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        costSummary.map(row => (
                          <TableRow key={row.date}>
                            <TableCell className="font-medium">{row.displayDate}</TableCell>
                            <TableCell className="text-right tabular-nums">{currencySymbol}{row.shiftCost.toFixed(2)}</TableCell>
                            <TableCell className="text-right tabular-nums">{currencySymbol}{row.salaryCost.toFixed(2)}</TableCell>
                            <TableCell className="text-right tabular-nums">{currencySymbol}{row.absenceCost.toFixed(2)}</TableCell>
                            <TableCell className="text-right tabular-nums font-medium">{currencySymbol}{row.totalCost.toFixed(2)}</TableCell>
                          </TableRow>
                        ))
                      )}
                      {costSummary.length > 0 && (
                        <TableRow className="bg-muted/50 font-medium">
                          <TableCell colSpan={groupBy === "employee" ? 2 : 1}>Total</TableCell>
                          <TableCell className="text-right tabular-nums">{currencySymbol}{totals.totalShiftCost.toFixed(2)}</TableCell>
                          <TableCell className="text-right tabular-nums">{currencySymbol}{totals.totalSalaryCost.toFixed(2)}</TableCell>
                          <TableCell className="text-right tabular-nums">{currencySymbol}{totals.totalAbsenceCost.toFixed(2)}</TableCell>
                          <TableCell className="text-right tabular-nums">{currencySymbol}{totals.totalCost.toFixed(2)}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TabsContent>

                {/* Shift Details Tab */}
                <TabsContent value="shifts" className="m-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Employee</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead>Rate</TableHead>
                        <TableHead className="text-right">Breaks</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shiftDetails.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                            No shifts for this period
                          </TableCell>
                        </TableRow>
                      ) : (
                        shiftDetails.map(row => {
                          const color = getColorById(row.employeeColor);
                          return (
                            <TableRow key={row.id}>
                              <TableCell className="font-medium">{row.displayDate}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium", color?.bg, color?.text)}>
                                    {row.employeeName.charAt(0)}
                                  </div>
                                  <span>{row.employeeName}</span>
                                </div>
                              </TableCell>
                              <TableCell className="tabular-nums text-sm">{row.startTime} - {row.endTime}</TableCell>
                              <TableCell className="text-right tabular-nums">{row.billableHours.toFixed(2)}h</TableCell>
                              <TableCell>
                                {row.rateName !== "Standard" && (
                                  <Badge variant="secondary" className="text-xs">{row.rateName}</Badge>
                                )}
                                {row.rateName === "Standard" && <span className="text-muted-foreground text-sm">Standard</span>}
                              </TableCell>
                              <TableCell className="text-right">{row.breakCount > 0 ? row.breakCount : "-"}</TableCell>
                              <TableCell className="text-right tabular-nums">{currencySymbol}{row.cost.toFixed(2)}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TabsContent>

                {/* Absences Tab */}
                <TabsContent value="absences" className="m-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {groupBy === "employee" ? (
                          <>
                            <TableHead>Employee</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead className="text-right">Total Days</TableHead>
                            <TableHead className="text-right">Paid</TableHead>
                            <TableHead className="text-right">Unpaid</TableHead>
                            <TableHead className="text-right">Cost</TableHead>
                          </>
                        ) : (
                          <>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Employees Absent</TableHead>
                            <TableHead className="text-right">Paid</TableHead>
                            <TableHead className="text-right">Unpaid</TableHead>
                            <TableHead className="text-right">Cost</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {absenceSummary.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                            No absences for this period
                          </TableCell>
                        </TableRow>
                      ) : groupBy === "employee" ? (
                        absenceSummary.map(row => {
                          const color = getColorById(row.color);
                          return (
                            <TableRow key={row.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium", color?.bg, color?.text)}>
                                    {row.name.charAt(0)}
                                  </div>
                                  <span className="font-medium">{row.name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground">{row.role}</TableCell>
                              <TableCell className="text-right tabular-nums">{row.totalDays}</TableCell>
                              <TableCell className="text-right">
                                {row.paidDays > 0 && (
                                  <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
                                    {row.paidDays}
                                  </Badge>
                                )}
                                {row.paidDays === 0 && "-"}
                              </TableCell>
                              <TableCell className="text-right">
                                {row.unpaidDays > 0 && (
                                  <Badge variant="outline" className="bg-gray-50 text-gray-600 dark:bg-gray-900 dark:text-gray-400">
                                    {row.unpaidDays}
                                  </Badge>
                                )}
                                {row.unpaidDays === 0 && "-"}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{currencySymbol}{row.totalCost.toFixed(2)}</TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        absenceSummary.map(row => (
                          <TableRow key={row.date}>
                            <TableCell className="font-medium">{row.displayDate}</TableCell>
                            <TableCell className="text-right">{row.employeesAbsent}</TableCell>
                            <TableCell className="text-right">
                              {row.paidCount > 0 && (
                                <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
                                  {row.paidCount}
                                </Badge>
                              )}
                              {row.paidCount === 0 && "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              {row.unpaidCount > 0 && (
                                <Badge variant="outline" className="bg-gray-50 text-gray-600 dark:bg-gray-900 dark:text-gray-400">
                                  {row.unpaidCount}
                                </Badge>
                              )}
                              {row.unpaidCount === 0 && "-"}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{currencySymbol}{row.totalCost.toFixed(2)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TabsContent>

                {/* Events Summary Tab */}
                <TabsContent value="events" className="m-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {groupBy === "employee" ? (
                          <>
                            <TableHead>Event Name</TableHead>
                            <TableHead className="text-right">Occurrences</TableHead>
                            <TableHead className="text-right">Total Hours</TableHead>
                          </>
                        ) : (
                          <>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Events</TableHead>
                            <TableHead className="text-right">Total Hours</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eventsSummary.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                            No events for this period
                          </TableCell>
                        </TableRow>
                      ) : groupBy === "employee" ? (
                        <>
                          {eventsSummary.map((row, index) => {
                            const colorMap = {
                              blue: "bg-blue-500",
                              green: "bg-green-500",
                              purple: "bg-purple-500",
                              red: "bg-red-500",
                              orange: "bg-orange-500",
                              yellow: "bg-yellow-500",
                              teal: "bg-teal-500",
                              pink: "bg-pink-500",
                            };
                            const dotColor = colorMap[row.color] || colorMap.blue;
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <div className={cn("w-3 h-3 rounded-full", dotColor)} />
                                    <span className="font-medium">{row.name}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-right tabular-nums">{row.count}</TableCell>
                                <TableCell className="text-right tabular-nums">{row.totalHours.toFixed(2)}h</TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Total row */}
                          <TableRow className="bg-muted/50 font-medium">
                            <TableCell>Total</TableCell>
                            <TableCell className="text-right tabular-nums">{totals.totalEvents}</TableCell>
                            <TableCell className="text-right tabular-nums">{totals.totalEventHours.toFixed(2)}h</TableCell>
                          </TableRow>
                        </>
                      ) : (
                        <>
                          {eventsSummary.map(row => (
                            <TableRow key={row.date}>
                              <TableCell className="font-medium">{row.displayDate}</TableCell>
                              <TableCell className="text-right tabular-nums">{row.eventCount}</TableCell>
                              <TableCell className="text-right tabular-nums">{row.totalHours.toFixed(2)}h</TableCell>
                            </TableRow>
                          ))}
                          {/* Total row */}
                          <TableRow className="bg-muted/50 font-medium">
                            <TableCell>Total</TableCell>
                            <TableCell className="text-right tabular-nums">{totals.totalEvents}</TableCell>
                            <TableCell className="text-right tabular-nums">{totals.totalEventHours.toFixed(2)}h</TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TabsContent>
              </>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Upgrade Modal */}
      <Dialog open={upgradeModal.open} onOpenChange={(open) => setUpgradeModal({ ...upgradeModal, open })}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <DialogTitle className="text-center">Upgrade to Standard</DialogTitle>
            <DialogDescription className="text-center">
              {upgradeModal.feature && FEATURE_METADATA[upgradeModal.feature] && (
                <><span className="font-medium text-foreground">{FEATURE_METADATA[upgradeModal.feature].name}</span> requires the Standard plan or higher.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
            <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">Standard Plan</span>
                <span className="text-2xl font-bold">£29<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
              </div>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">Wage Cost Calculations</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">Absence & Leave Tracking</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">Advanced Reports</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">CSV & PDF Exports</span>
                </li>
                <li className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-muted-foreground">Break Management</span>
                </li>
              </ul>
            </div>

            <Button className="w-full bg-blue-600 hover:bg-blue-700" size="lg">
              Upgrade Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            <p className="text-center text-xs text-muted-foreground">
              30-day money-back guarantee • Cancel anytime
            </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
